import { Injectable, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpHeaderService} from './http-header.service';
import {Member} from '../model/member';
import {MemberUpdate} from '../model/member-update';
import {APP_CONFIG} from '../config/app-config.token';
import {AppConfig} from '../config/app-config.model';
import {MembershipFee} from '../model/membership-fee';
import {MembershipOverview} from '../model/membership-overview';
import {CapacitorHttpService} from './capacitor-http.service';

@Injectable({
  providedIn: 'root'
})
export class MemberService {
  private http = inject(HttpClient);
  private capacitorHttp = inject(CapacitorHttpService);
  private httpHeaderService = inject(HttpHeaderService);
  private config = inject<AppConfig>(APP_CONFIG);

  baseurl: string;

  constructor() {
    this.baseurl = this.config.apiGatewayUrl;
  }

  /**
   * Get member data by INSS number.
   * FOR INTERNAL USERS ONLY - to search and view specific member data.
   * External users should use getMyMember() instead.
   */
  getMember(memberId: string): Observable<Member> {
    return this.capacitorHttp.get<Member>(this.baseurl + `/members/${memberId}`, this.httpHeaderService.getHeaderOptions());
  }

  /**
   * Get current user's member data.
   * FOR EXTERNAL USERS - uses /members/me endpoint which Tyk rewrites to /members/{jwt.preferred_username}
   * This is the ONLY way external users can access member data (security jail).
   */
  getMyMember(): Observable<Member> {
    return this.capacitorHttp.get<Member>(this.baseurl + '/members/me', this.httpHeaderService.getHeaderOptions());
  }

  /**
   * Update member data by INSS number.
   * FOR INTERNAL USERS ONLY - to update specific member data.
   * External users should use updateMyMember() instead.
   */
  updateMember(memberId: string, memberUpdate: MemberUpdate): Observable<Member> {
    return this.capacitorHttp.put<Member>(this.baseurl + `/members/${memberId}`, memberUpdate, this.httpHeaderService.getHeaderOptions());
  }

  /**
   * Update current user's member data.
   * FOR EXTERNAL USERS - uses /members/me endpoint which Tyk rewrites to /members/{jwt.preferred_username}
   */
  updateMyMember(memberUpdate: MemberUpdate): Observable<Member> {
    return this.capacitorHttp.put<Member>(this.baseurl + '/members/me', memberUpdate, this.httpHeaderService.getHeaderOptions());
  }

  /**
   * Get membership fees by INSS number.
   * FOR INTERNAL USERS ONLY - to view specific member's fees.
   * External users should use getMyMemberShipFees() instead.
   */
  getMemberShipFees(memberId: string): Observable<MembershipFee[]> {
    return this.capacitorHttp.get<MembershipFee[]>(
      this.baseurl + `/members/${memberId}/membershipfees`, this.httpHeaderService.getHeaderOptions());
  }

  /**
   * Get current user's membership fees.
   * FOR EXTERNAL USERS - uses /members/me/membershipfees endpoint which Tyk rewrites to /members/{jwt.preferred_username}/membershipfees
   */
  getMyMemberShipFees(): Observable<MembershipFee[]> {
    return this.capacitorHttp.get<MembershipFee[]>(this.baseurl + '/members/me/membershipfees', this.httpHeaderService.getHeaderOptions());
  }

  /**
   * Get membership overview by INSS number.
   * FOR INTERNAL USERS ONLY - to view specific member's membership.
   * External users should use getMyMemberShip() instead.
   */
  getMemberShip(memberId: string): Observable<MembershipOverview> {
    return this.capacitorHttp.get<MembershipOverview>(
      this.baseurl + `/members/${memberId}/memberships`, this.httpHeaderService.getHeaderOptions());
  }

  /**
   * Get current user's membership overview.
   * FOR EXTERNAL USERS - uses /members/me/memberships endpoint which Tyk rewrites to /members/{jwt.preferred_username}/memberships
   */
  getMyMemberShip(): Observable<MembershipOverview> {
    return this.capacitorHttp.get<MembershipOverview>(this.baseurl + '/members/me/memberships', this.httpHeaderService.getHeaderOptions());
  }

}
